import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';
import { useMilestoneActions } from '@/hooks/useMilestoneActions';
import { useActionLinkedHabits } from '@/hooks/useActionLinkedHabits';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { MilestoneActionItem } from './MilestoneActionItem';
import { AddHabitToActionDialog } from './AddHabitToActionDialog';
import { Map, Sparkles, Loader2, ChevronDown, Clock, Target, Lightbulb, Zap, ListChecks, Check, X, Pencil, CheckCircle2, Lock } from 'lucide-react';
import { TabStatsLine } from './TabStatsLine';
import { MilestoneProgressRing } from './MilestoneProgressRing';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { getTierFromProfile, canRegeneratePlan, incrementUsage, type UserTier } from '@/lib/tier-utils';
import { UpgradeModal } from '@/components/subscription/UpgradeModal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { track, trackLimitHit } from '@/lib/analytics';
import { useDateFormat } from '@/contexts/DateFormatContext';
import { BETA_CONFIG } from '@/config/beta';

interface SuggestedHabit {
  title: string;
  frequency: 'daily' | 'weekly';
  rationale: string;
}

interface Milestone {
  title: string;
  description: string;
  timeframe: string;
  actions: string[];
  skills_to_develop?: string[];
  success_metrics?: string[];
  suggested_habits?: SuggestedHabit[];
}

interface DevPlan {
  id: string;
  title: string;
  summary: string | null;
  milestones: Milestone[];
  is_active: boolean;
  generated_at: string;
}

interface ProfileData {
  job_role: string | null;
  target_job_role: string | null;
  skills: string[] | null;
  last_skill_gap_analysis: SkillGapAnalysis | null;
}

interface SkillGapAnalysis {
  summary?: string;
  skill_gaps?: {
    skill: string;
    importance: string;
    current_level: string;
    target_level: string;
    recommendations?: {
      education?: string[];
      exposure?: string[];
      experience?: string[];
    };
  }[];
  quick_wins?: string[];
  timeline_estimate?: string;
}

interface Goal {
  title: string;
  progress: number;
  e_types: string[];
}

interface CoreValue {
  value_name: string;
  description: string | null;
}

interface Habit {
  id: string;
  title: string;
  streak: number;
}

interface PendingHabit {
  milestoneIndex: number;
  habitIndex: number;
  habit: SuggestedHabit;
}

interface DevPlanTabProps {
  expandedMilestoneIndex?: number | null;
  onMilestoneExpanded?: () => void;
  onPlanGenerated?: () => void;
  onHabitsAccepted?: () => void;
}

export const DevPlanTab = ({ expandedMilestoneIndex, onMilestoneExpanded, onPlanGenerated, onHabitsAccepted }: DevPlanTabProps = {}) => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { toast } = useToast();
  const { formatDate } = useDateFormat();
  const [plan, setPlan] = useState<DevPlan | null>(null);
  const [habits, setHabits] = useState<Habit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedMilestone, setExpandedMilestone] = useState<number | null>(0);
  
  // Tier-based limits state
  const [userTier, setUserTier] = useState<UserTier>('free');
  const [canRegenerate, setCanRegenerate] = useState(true);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [pendingHabits, setPendingHabits] = useState<PendingHabit[]>([]);
  const [habitsDismissed, setHabitsDismissed] = useState(false);
  const [acceptingHabit, setAcceptingHabit] = useState<string | null>(null);
  const [editingHabit, setEditingHabit] = useState<string | null>(null);
  const [editedTitle, setEditedTitle] = useState<string>('');
  const [highlightHabits, setHighlightHabits] = useState(false);
  const suggestedHabitsRef = useRef<HTMLDivElement>(null);
  
  const { actions: milestoneActionsData, isActionCompleted, toggleAction, getMilestoneProgress } = useMilestoneActions(plan?.id || null);
  const { linkedHabits, calculateEnhancedProgress, getActionProgress, milestoneActionsMap, refetch: refetchLinkedHabits } = useActionLinkedHabits(plan?.id || null);
  
  // State for add habit dialog
  const [addHabitDialog, setAddHabitDialog] = useState<{
    isOpen: boolean;
    milestoneIndex: number;
    actionIndex: number;
    actionText: string;
  } | null>(null);

  // Get linked habits for a specific action
  const getLinkedHabitsForAction = useCallback((milestoneIndex: number, actionIndex: number) => {
    const action = milestoneActionsData.find(
      a => a.milestone_index === milestoneIndex && a.action_index === actionIndex
    );
    if (!action) return [];
    return linkedHabits[action.id] || [];
  }, [milestoneActionsData, linkedHabits]);

  // Handle external navigation to expand a specific milestone
  useEffect(() => {
    if (expandedMilestoneIndex !== null && expandedMilestoneIndex !== undefined) {
      setExpandedMilestone(expandedMilestoneIndex);
      onMilestoneExpanded?.();
    }
  }, [expandedMilestoneIndex, onMilestoneExpanded]);

  // Fetch regeneration status (tier comes from useProfile cache)
  const fetchRegenStatus = useCallback(async () => {
    if (!user) return;
    setUserTier(getTierFromProfile(profile));
    const regenResult = await canRegeneratePlan(user.id);
    setCanRegenerate(regenResult.allowed);
  }, [user, profile]);

  // Derive skill gap analysis from cached profile
  const skillGapAnalysis = (profile?.last_skill_gap_analysis as unknown as SkillGapAnalysis) || null;

  useEffect(() => {
    fetchActivePlan();
    fetchHabits();
    fetchRegenStatus();
  }, [user]);

  // Re-derive pending habits from plan data when mounting or after accepting individual habits
  useEffect(() => {
    if (!plan || habitsDismissed) return;
    const allSuggested: PendingHabit[] = [];
    plan.milestones.forEach((milestone, mIndex) => {
      milestone.suggested_habits?.forEach((habit, hIndex) => {
        allSuggested.push({ milestoneIndex: mIndex, habitIndex: hIndex, habit });
      });
    });
    // Filter out habits already accepted (match by title)
    const habitTitles = new Set(habits.map(h => h.title.toLowerCase()));
    const remaining = allSuggested
      .filter(p => !habitTitles.has(p.habit.title.toLowerCase()))
      .slice(0, 5);
    setPendingHabits(remaining);
  }, [plan, habits, habitsDismissed]);

  const fetchHabits = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('habits')
      .select('id, title, streak')
      .in('goal_id', (await supabase.from('goals').select('id').eq('user_id', user.id)).data?.map(g => g.id) || []);
    if (data) {
      setHabits(data as unknown as Habit[]);
    }
  }, [user]);

  // Find habits that might match action items (simple keyword matching)
  const findMatchingHabit = (action: string): Habit | null => {
    const actionWords = action.toLowerCase().split(/\s+/);
    return habits.find((habit) => {
      const habitWords = habit.title.toLowerCase().split(/\s+/);
      return actionWords.some((word) => 
        word.length > 3 && habitWords.some((hw) => hw.includes(word) || word.includes(hw))
      );
    }) || null;
  };

  // Find skill gaps that match the milestone's skills_to_develop
  const getMatchingSkillGaps = (skillsToDevelope: string[] | undefined) => {
    if (!skillGapAnalysis?.skill_gaps || !skillsToDevelope?.length) return [];
    
    return skillGapAnalysis.skill_gaps.filter(gap => {
      const gapSkillLower = gap.skill.toLowerCase();
      return skillsToDevelope.some(skill => {
        const skillLower = skill.toLowerCase();
        // Check for partial matches
        return gapSkillLower.includes(skillLower) || 
               skillLower.includes(gapSkillLower) ||
               // Check word overlap
               gapSkillLower.split(/\s+/).some(word => 
                 word.length > 3 && skillLower.includes(word)
               );
      });
    });
  };

  const getImportanceColor = (importance: string) => {
    switch (importance.toLowerCase()) {
      case 'high':
        return 'bg-destructive/10 text-destructive border-destructive/30';
      case 'medium':
        return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30';
      default:
        return 'bg-muted text-muted-foreground border-muted';
    }
  };

  const fetchActivePlan = async () => {
    if (!user) return;
    setIsLoading(true);
    
    const { data, error } = await supabase
      .from('development_plans')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('generated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (data && !error) {
      // Parse milestones from JSONB
      const parsedPlan: DevPlan = {
        ...data,
        milestones: Array.isArray(data.milestones) ? (data.milestones as unknown as Milestone[]) : [],
      };
      setPlan(parsedPlan);
    }
    setIsLoading(false);
  };

  const generatePlan = async (isRegeneration = false) => {
    if (!user) return;
    
    // Check regeneration limits for free users when regenerating
    if (!BETA_CONFIG.allFeaturesUnlocked && isRegeneration && userTier === 'free') {
      const regenCheck = await canRegeneratePlan(user.id);
      if (!regenCheck.allowed) {
        trackLimitHit('plan', {});
        setUpgradeModalOpen(true);
        return;
      }
    }
    
    setIsGenerating(true);

    try {
      // Fetch user context (profile comes from useProfile cache)
      const [goalsRes, valuesRes] = await Promise.all([
        supabase.from('goals').select('title, progress, e_types').eq('user_id', user.id),
        supabase.from('core_values').select('value_name, description').eq('user_id', user.id),
      ]);

      const profileForPlan: ProfileData = profile
        ? {
            job_role: profile.job_role,
            target_job_role: profile.target_job_role,
            skills: profile.skills,
            last_skill_gap_analysis: profile.last_skill_gap_analysis as SkillGapAnalysis | null,
          }
        : { job_role: null, target_job_role: null, skills: null, last_skill_gap_analysis: null };

      const goals: Goal[] = (goalsRes.data || []).map(g => ({
        title: g.title,
        progress: g.progress,
        e_types: g.e_types || [],
      }));
      const coreValues: CoreValue[] = (valuesRes.data || []).map(v => ({
        value_name: v.value_name,
        description: v.description,
      }));

      // Get authenticated session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-dev-plan`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            jobRole: profileForPlan.job_role || '',
            targetJobRole: profileForPlan.target_job_role || '',
            skills: profileForPlan.skills || [],
            goals,
            coreValues,
            skillGapAnalysis: profileForPlan.last_skill_gap_analysis,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 429 && errorData.retry_after_seconds) {
          const seconds = errorData.retry_after_seconds;
          const timeMsg = seconds >= 60 ? `${Math.ceil(seconds / 60)} minute(s)` : `${seconds} seconds`;
          throw new Error(`Too many requests. Please try again in ${timeMsg}.`);
        }
        throw new Error(errorData.error || 'Failed to generate plan');
      }

      const planData = await response.json();

      // Deactivate old plans
      await supabase
        .from('development_plans')
        .update({ is_active: false })
        .eq('user_id', user.id);

      // Save new plan
      const { data: newPlan, error } = await supabase
        .from('development_plans')
        .insert({
          user_id: user.id,
          title: planData.title,
          summary: planData.summary,
          milestones: planData.milestones,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;

      const parsedNewPlan: DevPlan = {
        ...newPlan,
        milestones: Array.isArray(newPlan.milestones) ? (newPlan.milestones as unknown as Milestone[]) : [],
      };
      setPlan(parsedNewPlan);
      onPlanGenerated?.();

      // Extract suggested habits for user to accept/reject
      const suggestedHabits: PendingHabit[] = [];
      parsedNewPlan.milestones.forEach((milestone, mIndex) => {
        milestone.suggested_habits?.forEach((habit, hIndex) => {
          suggestedHabits.push({
            milestoneIndex: mIndex,
            habitIndex: hIndex,
            habit,
          });
        });
      });
      // Cap at 5 habits to avoid overwhelming new users
      const cappedHabits = suggestedHabits.slice(0, 5);
      setPendingHabits(cappedHabits);

      if (cappedHabits.length > 0) {
        toast({
          title: 'Development plan generated!',
          description: `${cappedHabits.length} habit suggestions ready for review.`,
        });
        
        // Scroll to and highlight the suggested habits section after a brief delay
        setTimeout(() => {
          suggestedHabitsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightHabits(true);
          // Remove highlight after animation
          setTimeout(() => setHighlightHabits(false), 2000);
        }, 300);
      } else {
        toast({ title: 'Development plan generated!' });
      }
      
      // Increment usage for free users on first generation
      if (userTier === 'free') {
        await incrementUsage(user.id, 'plan_generations');
        await fetchRegenStatus(); // Refresh limit status
      }
      
      // Track plan generation (fire-and-forget)
      track('plan_generated', { tier: userTier, isRegeneration });
    } catch (error) {
      toast({
        title: 'Error generating plan',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Wrapper functions for button clicks
  const handleGeneratePlan = () => generatePlan(false);
  const handleRegeneratePlan = () => generatePlan(true);

  // Accept a suggested habit - create it as a real habit
  const acceptHabit = async (pending: PendingHabit) => {
    if (!user) return;
    
    const key = `${pending.milestoneIndex}-${pending.habitIndex}`;
    setAcceptingHabit(key);

    try {
      // First, we need a goal to attach the habit to
      const milestone = plan?.milestones[pending.milestoneIndex];
      if (!milestone) throw new Error('Milestone not found');

      // Check if there's an existing goal that matches the milestone
      const { data: existingGoals } = await supabase
        .from('goals')
        .select('id, title')
        .eq('user_id', user.id);

      let goalId: string | null = null;
      if (existingGoals && existingGoals.length > 0) {
        // Look for a goal with similar keywords
        const milestoneWords = milestone.title.toLowerCase().split(/\s+/);
        const matchingGoal = existingGoals.find(g => {
          const goalWords = g.title.toLowerCase().split(/\s+/);
          return milestoneWords.some(mw => mw.length > 3 && goalWords.some(gw => gw.includes(mw) || mw.includes(gw)));
        });
        goalId = matchingGoal?.id || existingGoals[0].id;
      } else {
        // Create a new goal based on the milestone
        const { data: newGoal, error: goalError } = await supabase
          .from('goals')
          .insert({
            user_id: user.id,
            title: milestone.title,
            description: milestone.description,
            e_types: ['Experience'],
          })
          .select()
          .single();

        if (goalError) throw goalError;
        goalId = newGoal.id;
      }

      // Create the habit
      const { error: habitError } = await supabase
        .from('habits')
        .insert({
          goal_id: goalId,
          title: pending.habit.title,
          frequency: pending.habit.frequency,
        });

      if (habitError) throw habitError;

      // Remove from pending
      setPendingHabits(prev => prev.filter(p => 
        !(p.milestoneIndex === pending.milestoneIndex && p.habitIndex === pending.habitIndex)
      ));

      fetchHabits();
      onHabitsAccepted?.();
      toast({
        title: 'Habit added!',
        description: `"${pending.habit.title}" is now in your habit tracker.`,
      });
    } catch (error) {
      toast({
        title: 'Error adding habit',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setAcceptingHabit(null);
    }
  };

  const dismissHabit = (pending: PendingHabit) => {
    setPendingHabits(prev => prev.filter(p => 
      !(p.milestoneIndex === pending.milestoneIndex && p.habitIndex === pending.habitIndex)
    ));
  };

  const startEditingHabit = (pending: PendingHabit) => {
    const key = `${pending.milestoneIndex}-${pending.habitIndex}`;
    setEditingHabit(key);
    setEditedTitle(pending.habit.title);
  };

  const saveEditedTitle = (pending: PendingHabit) => {
    if (!editedTitle.trim()) return;
    
    setPendingHabits(prev => prev.map(p => {
      if (p.milestoneIndex === pending.milestoneIndex && p.habitIndex === pending.habitIndex) {
        return {
          ...p,
          habit: { ...p.habit, title: editedTitle.trim() }
        };
      }
      return p;
    }));
    setEditingHabit(null);
    setEditedTitle('');
  };

  const cancelEditing = () => {
    setEditingHabit(null);
    setEditedTitle('');
  };

  const toggleHabitFrequency = (pending: PendingHabit) => {
    setPendingHabits(prev => prev.map(p => {
      if (p.milestoneIndex === pending.milestoneIndex && p.habitIndex === pending.habitIndex) {
        return {
          ...p,
          habit: { 
            ...p.habit, 
            frequency: p.habit.frequency === 'daily' ? 'weekly' : 'daily' 
          }
        };
      }
      return p;
    }));
  };

  const acceptAllHabits = async () => {
    for (const pending of pendingHabits) {
      await acceptHabit(pending);
    }
    onHabitsAccepted?.();
  };

  const dismissAllHabits = () => {
    setHabitsDismissed(true);
    setPendingHabits([]);
    toast({ title: 'All suggestions dismissed' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!plan) {
    const missingRoles = !profile?.job_role && !profile?.target_job_role;
    return (
      <Card className="card-elevated">
        <CardContent className="py-16 text-center">
          <Map className="w-16 h-16 mx-auto mb-4 text-primary/50" />
          <h3 className="text-xl font-semibold mb-2">No Development Plan Yet</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Generate a personalized development plan based on your goals, skills, and career aspirations.
            The more you fill out your profile and goals, the better the plan!
          </p>
          {missingRoles && (
            <Alert className="mb-6 max-w-md mx-auto text-left">
              <Lightbulb className="h-4 w-4" />
              <AlertTitle>Add your role for a better plan</AlertTitle>
              <AlertDescription>
                Add your job role and target role in{' '}
                <a href="/settings" className="text-primary underline hover:text-primary/80">Settings</a>{' '}
                so the AI can create a relevant development plan.
              </AlertDescription>
            </Alert>
          )}
          <Button onClick={handleGeneratePlan} disabled={isGenerating} className="gap-2">
            {isGenerating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {isGenerating ? 'Generating...' : 'Generate My Plan'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Calculate overall stats
  const planStats = (() => {
    const enhancedProgress = calculateEnhancedProgress(plan.milestones, milestoneActionsData);
    const totalActions = plan.milestones.reduce((sum, m) => sum + m.actions.length, 0);
    return {
      milestones: plan.milestones.length,
      totalActions,
      completed: enhancedProgress.completed,
      percentage: enhancedProgress.percentage,
    };
  })();

  return (
    <div className="space-y-6">
      {/* Plan Header */}
      <Card className="card-elevated">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Map className="w-5 h-5 text-primary" />
                {plan.title}
              </CardTitle>
              <CardDescription className="mt-2">{plan.summary}</CardDescription>
              <TabStatsLine
                stats={[
                  { value: planStats.milestones, label: 'milestones', icon: <Target className="w-3.5 h-3.5" /> },
                  { value: `${planStats.completed}/${planStats.totalActions}`, label: 'actions complete', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
                  { value: `${planStats.percentage}%`, label: 'progress', icon: <Zap className="w-3.5 h-3.5" /> },
                ]}
              />
            </div>
            {/* Regenerate button with tier-based limits */}
            {!BETA_CONFIG.allFeaturesUnlocked && userTier === 'free' && !canRegenerate ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setUpgradeModalOpen(true)}
                      className="gap-1"
                    >
                      <Lock className="w-4 h-4" />
                      <span className="hidden sm:inline">Regenerate</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Upgrade to Pro to regenerate your plan</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Button variant="outline" size="sm" onClick={handleRegeneratePlan} disabled={isGenerating}>
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                <span className="ml-2 hidden sm:inline">Regenerate</span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span>{plan.milestones.length} milestones</span>
            <span>•</span>
            <span>Generated {formatDate(plan.generated_at)}</span>
            {skillGapAnalysis?.skill_gaps && skillGapAnalysis.skill_gaps.length > 0 && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1 text-primary">
                  <Zap className="w-3 h-3" />
                  {skillGapAnalysis.skill_gaps.length} skill gaps addressed
                </span>
              </>
            )}
          </div>
          {/* Overall Progress */}
          {(() => {
            const enhancedProgress = calculateEnhancedProgress(plan.milestones, milestoneActionsData);
            return (
              <div className="space-y-2">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-sm">
                  <span className="text-muted-foreground">Overall Progress</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">
                      {enhancedProgress.completed}/{enhancedProgress.total} actions ({enhancedProgress.percentage}%)
                    </span>
                    {enhancedProgress.habitCompletionsThisWeek > 0 && (
                      <>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-primary font-medium">
                          {enhancedProgress.habitCompletionsThisWeek} habit completions this week
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <Progress value={enhancedProgress.percentage} className="h-2" />
                {enhancedProgress.actionsWithHabitProgress > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Including progress from {enhancedProgress.actionsWithHabitProgress} action{enhancedProgress.actionsWithHabitProgress > 1 ? 's' : ''} with daily habits
                  </p>
                )}
              </div>
              );
            })()}
            
            {/* Upgrade prompt for free users who can't regenerate */}
            {!BETA_CONFIG.allFeaturesUnlocked && userTier === 'free' && !canRegenerate && (
              <Alert className="border-amber-500/30 bg-amber-500/5 mt-4">
                <Lightbulb className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-700">Goals changed?</AlertTitle>
                <AlertDescription className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <span className="text-sm">
                    Upgrade to Pro to regenerate your plan with updated priorities.
                  </span>
                  <Button 
                    size="sm" 
                    onClick={() => setUpgradeModalOpen(true)}
                    className="gap-1 shrink-0 bg-gradient-to-r from-primary to-accent"
                  >
                    <Sparkles className="w-3 h-3" />
                    Upgrade
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

      {/* Pending Habit Suggestions */}
      {pendingHabits.length > 0 && (
        <Card 
          ref={suggestedHabitsRef}
          className={`card-elevated border-accent/30 bg-accent/5 transition-all duration-500 ${
            highlightHabits ? 'ring-2 ring-accent ring-offset-2 ring-offset-background animate-pulse' : ''
          }`}
        >
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ListChecks className="w-5 h-5 text-accent" />
                  Suggested Habits
                </CardTitle>
                <CardDescription>
                  {pendingHabits.length} habit{pendingHabits.length > 1 ? 's' : ''} suggested based on your development plan
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={dismissAllHabits}
                  className="text-xs"
                >
                  Dismiss All
                </Button>
                <Button 
                  size="sm" 
                  onClick={acceptAllHabits}
                  className="text-xs gap-1"
                >
                  <Check className="w-3 h-3" />
                  Accept All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {pendingHabits.map((pending) => {
                const key = `${pending.milestoneIndex}-${pending.habitIndex}`;
                const isAccepting = acceptingHabit === key;
                const isEditing = editingHabit === key;
                const milestone = plan.milestones[pending.milestoneIndex];
                
                return (
                  <div 
                    key={key}
                    className="flex items-center justify-between gap-3 p-3 bg-background rounded-lg border"
                  >
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editedTitle}
                            onChange={(e) => setEditedTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEditedTitle(pending);
                              if (e.key === 'Escape') cancelEditing();
                            }}
                            className="h-8 text-sm"
                            autoFocus
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 min-h-[44px] min-w-[44px] text-primary shrink-0"
                            onClick={() => saveEditedTitle(pending)}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 min-h-[44px] min-w-[44px] text-muted-foreground shrink-0"
                            onClick={cancelEditing}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{pending.habit.title}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 min-h-[44px] min-w-[44px] text-muted-foreground hover:text-primary"
                              onClick={() => startEditingHabit(pending)}
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Badge 
                              variant="outline" 
                              className="text-xs capitalize cursor-pointer hover:bg-accent transition-colors"
                              onClick={() => toggleHabitFrequency(pending)}
                              title="Click to toggle frequency"
                            >
                              {pending.habit.frequency}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            From: {milestone?.title} — {pending.habit.rationale}
                          </p>
                        </>
                      )}
                    </div>
                    {!isEditing && (
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 min-h-[44px] min-w-[44px] text-muted-foreground hover:text-destructive"
                          onClick={() => dismissHabit(pending)}
                          disabled={isAccepting}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 min-h-[44px] min-w-[44px] text-muted-foreground hover:text-primary"
                          onClick={() => acceptHabit(pending)}
                          disabled={isAccepting}
                        >
                          {isAccepting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Milestones */}
      <div className="space-y-4">
        {plan.milestones.map((milestone, index) => (
          <Collapsible
            key={index}
            open={expandedMilestone === index}
            onOpenChange={(open) => setExpandedMilestone(open ? index : null)}
          >
            <Card className="card-elevated">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Circular Progress Ring */}
                      {(() => {
                        // Combine manual completion with habit-based progress
                        const combinedProgress = milestone.actions.length > 0
                          ? Math.round(
                              milestone.actions.reduce((sum, _, aIdx) => {
                                const isManuallyCompleted = isActionCompleted(index, aIdx);
                                if (isManuallyCompleted) return sum + 100;
                                const actionHabitProgress = getActionProgress(index, aIdx, milestoneActionsMap);
                                return sum + (actionHabitProgress.habitProgress || 0);
                              }, 0) / milestone.actions.length
                            )
                          : 0;
                        return (
                          <>
                            {/* Desktop: 48px ring */}
                            <MilestoneProgressRing
                              progress={combinedProgress}
                              size={48}
                              strokeWidth={4}
                              className="hidden sm:flex"
                            />
                            {/* Mobile: 32px ring */}
                            <MilestoneProgressRing
                              progress={combinedProgress}
                              size={32}
                              strokeWidth={3}
                              className="flex sm:hidden"
                            />
                          </>
                        );
                      })()}
                      <div className="flex-1">
                        <CardTitle className="text-base">{milestone.title}</CardTitle>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {milestone.timeframe}
                          </Badge>
                          {(() => {
                            const progress = getMilestoneProgress(index, milestone.actions.length);
                            return progress.completed > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {progress.completed}/{progress.total} done
                              </Badge>
                            );
                          })()}
                          {/* Skill Gap Indicators */}
                          {(() => {
                            const matchingGaps = getMatchingSkillGaps(milestone.skills_to_develop);
                            return matchingGaps.length > 0 && (
                              <Badge variant="outline" className="text-xs bg-primary/5 text-primary border-primary/30">
                                <Zap className="w-3 h-3 mr-1" />
                                {matchingGaps.length} skill gap{matchingGaps.length > 1 ? 's' : ''}
                              </Badge>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${expandedMilestone === index ? 'rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-4">
                  <p className="text-muted-foreground">{milestone.description}</p>

                  {/* Skill Gap Connection Banner */}
                  {(() => {
                    const matchingGaps = getMatchingSkillGaps(milestone.skills_to_develop);
                    return matchingGaps.length > 0 && (
                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 space-y-2">
                        <h4 className="font-medium flex items-center gap-2 text-sm text-primary">
                          <Zap className="w-4 h-4" />
                          Addressing Skill Gaps
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {matchingGaps.map((gap, i) => (
                            <div 
                              key={i} 
                              className="flex items-center gap-2 bg-background rounded-md px-2 py-1 border"
                            >
                              <span className="text-sm font-medium">{gap.skill}</span>
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${getImportanceColor(gap.importance)}`}
                              >
                                {gap.importance}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {gap.current_level} → {gap.target_level}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Actions */}
                  <div>
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-primary" />
                      Action Items
                    </h4>
                    <ul className="space-y-3">
                      {milestone.actions.map((rawAction, actionIndex) => {
                        // Defensive: AI-generated JSONB actions may be objects instead of strings
                        const action = typeof rawAction === 'string' ? rawAction : (rawAction as any)?.action ?? String(rawAction);
                        return (
                        <MilestoneActionItem
                          key={actionIndex}
                          action={action}
                          completed={isActionCompleted(index, actionIndex)}
                          matchingHabit={findMatchingHabit(action)}
                          linkedHabits={getLinkedHabitsForAction(index, actionIndex)}
                          onToggle={() => toggleAction(index, actionIndex)}
                          onAddHabit={() => setAddHabitDialog({
                            isOpen: true,
                            milestoneIndex: index,
                            actionIndex,
                            actionText: action,
                          })}
                        />);
                      })}
                    </ul>
                  </div>

                  {/* Skills */}
                  {milestone.skills_to_develop && milestone.skills_to_develop.length > 0 && (
                    <div>
                      <h4 className="font-medium flex items-center gap-2 mb-2">
                        <Lightbulb className="w-4 h-4 text-accent" />
                        Skills to Develop
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {milestone.skills_to_develop.map((skill, i) => (
                          <Badge key={i} variant="secondary">{skill}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Success Metrics */}
                  {milestone.success_metrics && milestone.success_metrics.length > 0 && (
                    <div className="bg-muted/50 rounded-lg p-3">
                      <h4 className="font-medium text-sm mb-2">Success Metrics</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {milestone.success_metrics.map((metric, i) => (
                          <li key={i}>• {metric}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        ))}
      </div>

      {/* Add Habit to Action Dialog */}
      {addHabitDialog && plan && (
        <AddHabitToActionDialog
          isOpen={addHabitDialog.isOpen}
          onClose={() => setAddHabitDialog(null)}
          actionText={addHabitDialog.actionText}
          milestoneIndex={addHabitDialog.milestoneIndex}
          actionIndex={addHabitDialog.actionIndex}
          planId={plan.id}
          onHabitAdded={() => {
            refetchLinkedHabits();
            fetchHabits();
          }}
        />
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        trigger="plan_limit"
      />
    </div>
  );
};
